/* global getMilestonesUrl, minDate, maxDate */

import React, { Fragment, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import MilestoneSectionForm from './MilestoneSectionForm';

import { isSingleScreenCommitSection } from 'Helpers/procurementUtils';

const TICKETING_RESOURCE = 'ticketing';
const SPONSOR_RESOURCE = 'sponsor';
const EXPENSE_RESOURCE = 'expense';
const REVENUE_RESOURCE = 'revenue';
const PROJECT_RESOURCE = 'project';
const SECTION_CASH = 'cash';
const SECTION_VIK = 'vik';

export default function MilestonesEdit({
    vikEnabled = true,
    totalValue = 0,
    isRecalculateTotalAllowed = false,
    isSliderAllowed = true,
    allowConfirm = true,
    preserveDraftMilestones = false,
    ...props
}) {
    const [postRoute, setPostRoute] = useState('');
    const [redirectRoute, setRedirectRoute] = useState('');
    const [resource, setResource] = useState(null);
    const [milestones, setMilestones] = useState([]);
    const [canDeleteMilestones, setCanDeleteMilestones] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [sections, setSections] = useState(null);
    const [loading, setLoading] = useState(true);
    const [milestonesTotal, setMilestonesTotal] = useState(totalValue);
    const [resourceType, setResourceType] = useState(EXPENSE_RESOURCE);
    const [loadError, setLoadError] = useState(false);

    // A failed fetch used to be swallowed into console.log: `sections` stayed null, nothing rendered
    // at all, and on a single-screen commit the parent's gate ("no section has reported in") kept
    // Commit disabled for good with no way to tell why. Both ends are told instead.
    //
    // Reported unconditionally, unlike the inline banner below: the only consumer of this callback is
    // Commit Sponsor, which has no refetch path (no reloadSignal, allowConfirm={false}), so it can
    // only ever hear about the mount fetch. Any future consumer with a refetch should gate its own use.
    useEffect(() => {
        if (props.onLoadErrorChange) {
            props.onLoadErrorChange(loadError);
        }
    }, [loadError]);

    useEffect(() => {
        // Plain mount/load: preserve any saved draft milestones (rederive=false). IB-610.
        fetchData();
    }, []);

    useEffect(() => {
        // Fetch new totals again to reflect any changes after the completion of procurement process's step two.
        // Still a plain load — saved draft milestones must be preserved (rederive=false). IB-610.
        if (allowConfirm) {
            fetchData(true);
        }
    }, [allowConfirm]);

    // Re-derive the milestone draft whenever the parent signals that a line changed
    // (e.g. an expense was edited via the Direct Commitment modal). The first render is
    // skipped because the mount effect above already performs the initial fetch.
    const isFirstReload = useRef(true);
    useEffect(() => {
        if (isFirstReload.current) {
            isFirstReload.current = false;
            return;
        }
        // A line changed: re-derive the schedule from the new amounts (rederive=true). IB-610.
        fetchData(false, true);
    }, [props.reloadSignal]);

    useEffect(() => {
        setMilestonesTotal(totalValue);
    }, [totalValue]);

    useEffect(() => {
        if ((typeof sections === 'object' && sections !== null) || sections?.length) {
            if (props.setTotalMilestoneSections) {
                props.setTotalMilestoneSections(countItems(sections));
            }
            setLoading(false);
        }
    }, [sections]);

    const countItems = (x) => {
        if (Array.isArray(x)) {
            return x.length;
        }
        if (x && typeof x === 'object') {
            return Object.keys(x).length;
        }
        return 0;
    };

    const checkCashAndVikSections = (sections) => {
        const result = {
            hasCash: false,
            hasVik: false,
        };

        if (!sections) {
            return result;
        }

        let sectionKeys = [];

        // Handle array format: [{cash: 'Cash'}, {vik: 'VIK'}]
        if (Array.isArray(sections)) {
            sections.forEach((sectionObj) => {
                if (sectionObj && typeof sectionObj === 'object') {
                    sectionKeys.push(...Object.keys(sectionObj));
                }
            });
        }
        // Handle object format: {cash: 'Cash', vik: 'VIK'}
        else if (typeof sections === 'object') {
            sectionKeys = Object.keys(sections);
        }

        result.hasCash = sectionKeys.includes(SECTION_CASH);
        result.hasVik = sectionKeys.includes(SECTION_VIK);

        return result;
    };

    const getTotalAmount = (section) => {
        if (isRecalculateTotalAllowed) {
            if (resourceType === SPONSOR_RESOURCE) {
                return section === SECTION_CASH ? resource.uncommitted_cash : resource.uncommitted_vik;
            }

            if (resourceType === EXPENSE_RESOURCE && resource.type === PROJECT_RESOURCE) {
                return section === SECTION_CASH ? resource.price : resource.vik_allowance;
            }

            return milestonesTotal;
        }

        // Single-screen commit (Direct Commitment, Commit Ticketing, Commit Revenue): the value base is
        // the committing-totals source the parent form computed once (committingCash / committingVik
        // props), so the milestone Cash / VIK base always equals the impact headline and the table
        // footer. The %-allocation and 100% gate are unchanged — only the value BASE comes from the
        // shared source instead of the server's resource.uncommitted_cash / uncommitted_vik.
        if (
            isSingleScreenCommitSection(props.parentSection) &&
            [EXPENSE_RESOURCE, TICKETING_RESOURCE, REVENUE_RESOURCE, SPONSOR_RESOURCE].includes(resourceType)
        ) {
            const committing = section === SECTION_CASH ? props.committingCash : props.committingVik;
            return committing ?? (section === SECTION_CASH ? resource.uncommitted_cash : resource.uncommitted_vik);
        }

        return totalValue;
    };

    const fetchData = (skipMilestonesAllocation = false, rederive = false) => {
        // Only the DC / EA draft forms (preserveDraftMilestones) opt into the rederive flag so a
        // plain reopen keeps the user's saved milestones; every other consumer sends no param and
        // gets the legacy server default (rederive=true). IB-610.
        const params = preserveDraftMilestones ? { rederive: rederive ? 1 : 0 } : {};

        axios
            .get(props.getMilestonesUrl, { params })
            .then((response) => {
                const { data } = response;
                setLoadError(false);
                setPostRoute(data.postRoute);
                setRedirectRoute(data.redirectRoute);
                setResource(data.resource);
                // Skip milestones allocation if it is not the first fetch to keep the existing milestones
                if (!skipMilestonesAllocation) {
                    setMilestones(data.milestones);
                }
                setCanDeleteMilestones(data.canDeleteMilestones);
                setCanEdit(data.canEdit);
                setSections(data.sections);
                setResourceType(data.resourceType ?? EXPENSE_RESOURCE);
                if (totalValue == 0 && isRecalculateTotalAllowed && data.resourceType !== SPONSOR_RESOURCE) {
                    totalValue = data.resourceType === TICKETING_RESOURCE ? data.ticketingTotals.uncommitted : data.resource.cashflowObject.uncommitted || 0;
                    setMilestonesTotal(totalValue);
                }

                const checkCashAndVikSectionsResult = checkCashAndVikSections(data.sections);
                // If procurement does not have cash item then skip cash milestones
                if (!checkCashAndVikSectionsResult.hasCash && props.setCashMilestonesConfirmed) {
                    props.setCashMilestonesConfirmed(true);
                }

                // If procurement does not have vik item then skip vik milestones
                if (!checkCashAndVikSectionsResult.hasVik && props.setVikMilestonesConfirmed) {
                    props.setVikMilestonesConfirmed(true);
                }
            })
            .catch((e) => {
                console.log(e);
                setLoadError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Update the milestones for a specific section to keep track of the changes happening in milestones form
    // This is used to preserve the existing milestones when the user makes any changes in the procurement process's step two
    const updateSectionMilestones = (section, newSectionMilestones) => {
        setMilestones((prev) => [...prev.filter((m) => !m.type.includes(section)), ...newSectionMilestones]);
    };

    // sectionFilter is strictly OPT-IN: left undefined (every consumer except Commit Sponsor) all
    // served sections render exactly as before. Commit Sponsor is served both sections
    // unconditionally and narrows them here to the components it is actually committing, so a
    // component with nothing to commit does not demand a 100% schedule.
    const isSectionRendered = (section) =>
        (vikEnabled || section === SECTION_CASH) && (!Array.isArray(props.sectionFilter) || props.sectionFilter.includes(section));

    return (
        <Fragment>
            {loading ? (
                <div>{$t('Loading')}...</div>
            ) : loadError && !sections ? (
                // Only when there is nothing on screen to replace. A REFETCH that fails (DirectCommitForm's
                // reloadSignal on every expense edit, PurchaseOrdersCreateForm's allowConfirm at step 3)
                // leaves loading false and sections populated, and swapping the user's rendered milestone
                // tables for an error banner mid-flow would be worse than the silence this replaces. The
                // initial-load case — the one that leaves a commit screen permanently disabled — still
                // reports, because sections is null until the first successful fetch.
                <div className="tw-rounded-lg tw-border tw-border-red tw-bg-red-10 tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-red">
                    {$t('Payment milestones could not be loaded. Refresh the page to try again.')}
                </div>
            ) : Array.isArray(sections) ? (
                sections?.map((individualSection, index) => {
                    const section = Object.keys(individualSection).length ? Object.keys(individualSection)[0] : null;

                    const filtered = milestones.filter((item) => {
                        return item.type.includes(section);
                    });

                    return (
                        <Fragment key={index}>
                            {Object.entries(individualSection).map(([section, sectionTitle]) => {
                                return (
                                    <Fragment key={section}>
                                        {isSectionRendered(section) && (
                                            <MilestoneSectionForm
                                                postRoute={postRoute}
                                                redirectRoute={redirectRoute}
                                                resource={resource}
                                                resourceType={resourceType}
                                                milestones={filtered}
                                                canDeleteMilestones={canDeleteMilestones}
                                                canEdit={canEdit}
                                                section={section}
                                                sectionTitle={sectionTitle}
                                                minDate={props.minDate}
                                                maxDate={props.maxDate}
                                                submitButtonText={props.submitButtonText ? props.submitButtonText : $t('Update')}
                                                showCancelButton={props.showCancelButton}
                                                setMilestonesConfirmed={props.setMilestonesConfirmed}
                                                setVikMilestonesConfirmed={props.setVikMilestonesConfirmed}
                                                setCashMilestonesConfirmed={props.setCashMilestonesConfirmed}
                                                milestonesConfirmed={props.milestonesConfirmed}
                                                vikMilestonesConfirmed={props.vikMilestonesConfirmed}
                                                cashMilestonesConfirmed={props.cashMilestonesConfirmed}
                                                parentSection={props.parentSection ? props.parentSection : 'expense'}
                                                totalValue={getTotalAmount(section)}
                                                isSliderAllowed={isSliderAllowed}
                                                allowConfirm={allowConfirm}
                                                setMilestones={updateSectionMilestones}
                                                onValidityChange={props.onValidityChange}
                                                onSectionPresent={props.onSectionPresent}
                                                onAutosaveStateChange={props.onAutosaveStateChange}
                                                hideSectionHeader={props.hideSectionHeader}
                                            ></MilestoneSectionForm>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </Fragment>
                    );
                })
            ) : (
                typeof sections === 'object' &&
                sections !== null &&
                Object.entries(sections).map(([section, sectionTitle]) => {
                    const filtered = milestones.filter((item) => {
                        return item.type.includes(section);
                    });

                    return (
                        <Fragment key={section}>
                            {isSectionRendered(section) && (
                                <MilestoneSectionForm
                                    postRoute={postRoute}
                                    redirectRoute={redirectRoute}
                                    resource={resource}
                                    resourceType={resourceType}
                                    milestones={filtered}
                                    canDeleteMilestones={canDeleteMilestones}
                                    canEdit={canEdit}
                                    section={section}
                                    sectionTitle={sectionTitle}
                                    minDate={props.minDate}
                                    maxDate={props.maxDate}
                                    submitButtonText={props.submitButtonText ? props.submitButtonText : $t('Update')}
                                    showCancelButton={props.showCancelButton}
                                    setMilestonesConfirmed={props.setMilestonesConfirmed}
                                    setVikMilestonesConfirmed={props.setVikMilestonesConfirmed}
                                    setCashMilestonesConfirmed={props.setCashMilestonesConfirmed}
                                    milestonesConfirmed={props.milestonesConfirmed}
                                    vikMilestonesConfirmed={props.vikMilestonesConfirmed}
                                    cashMilestonesConfirmed={props.cashMilestonesConfirmed}
                                    parentSection={props.parentSection ? props.parentSection : 'expense'}
                                    totalValue={getTotalAmount(section)}
                                    isSliderAllowed={isSliderAllowed}
                                    allowConfirm={allowConfirm}
                                    setMilestones={updateSectionMilestones}
                                    onValidityChange={props.onValidityChange}
                                    onSectionPresent={props.onSectionPresent}
                                    onAutosaveStateChange={props.onAutosaveStateChange}
                                    hideSectionHeader={props.hideSectionHeader}
                                ></MilestoneSectionForm>
                            )}
                        </Fragment>
                    );
                })
            )}
        </Fragment>
    );
}

if (document.getElementById('procurementMilestonesEdit')) {
    var rootEl = document.getElementById('procurementMilestonesEdit');
    ReactDOM.render(
        <App>
            <MilestonesEdit getMilestonesUrl={getMilestonesUrl} minDate={minDate} maxDate={maxDate} isRecalculateTotalAllowed={true} isSliderAllowed={false} />
        </App>,
        rootEl,
    );
}
